import React, { useCallback, useMemo, useState } from 'react';
import './EmailEngagementTable.scss';

import {
  FlightTabs,
  FlightTabPanel,
  FlightTable,
  FlightSelect,
  FlightTextInput,
  FlightDateRangePicker,
  getIcon,
} from '@flybits/design-system';
import StatusBadge, { STATUS_TYPES } from 'components/Shared/StatusBadge/StatusBadge';
import { ReactComponent as SendGridProvider } from 'assets/icons/provider-sendgrid.svg';
import { ReactComponent as IconLastUpdated } from 'assets/icons/last-updated.svg';
import { ReactComponent as IconMessage } from 'assets/icons/email.svg';
import { epochToDateTimeString, intlNumberFormat, timeAgo } from 'helpers/common.helper';
import TotalHeader from './Shared/TotalHeader';
import { formatTotalValue } from 'pages/AnalyticsV2/helpers';

import AnalyticsExperiencesAPI from 'services/api/analytics-experiences.api';
import { AdvancedDateRange, DateRange } from 'pages/AnalyticsV2/types';
import {
  EmailDeliveryData,
  EmailDeliveryResponse,
  EmailEngagementData,
  EmailEngagementResponse,
  ENGAGEMENT_DATA_TYPES,
} from 'components/Analytics/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmationDialogProps } from 'components/Shared/shared.types';
import { ReactComponent as IconDownloadData } from 'assets/icons/icon-download-data.svg';
import useConfirmModal from 'hooks/useConfirmModal';
import DownloadEngagementData from './DownloadEngagementData';
import { useDispatch } from 'react-redux';
import useDebounce from 'hooks/useDebounce';
import { TableRow, TableHeader } from 'components/Analytics/types';

import useTableSort from 'hooks/useTableSort';
import { DATE_FORMAT_OPTIONS, TIME_CONTEXT } from '../constants';

const MAIN_CLASS = 'email-table';
const CLASSES = {
  HEADER: `${MAIN_CLASS}__header`,
  TITLE: `${MAIN_CLASS}__header__title`,
  TABS: `${MAIN_CLASS}__header__tabs`,
  TAB: `${MAIN_CLASS}__tabs__tab`,
  REPORT_TYPE: `${MAIN_CLASS}__report-type`,
  SEARCH: `${MAIN_CLASS}__search`,
  SEARCH_WRAPPER: `${MAIN_CLASS}__search__wrapper`,
  TOTALS: `${MAIN_CLASS}__totals`,
  DOWNLOAD: `${MAIN_CLASS}__header__download`,
  DATE_RANGE: `${MAIN_CLASS}__date-range`,
  DATE_RANGE_DD: `${MAIN_CLASS}__date-range__dropdown`,
  PROVIDER: `${MAIN_CLASS}__header__provider`,
  LAST_UPDATED: `${MAIN_CLASS}__last-updated`,
  EMPTY_DATA: `${MAIN_CLASS}__empty-data`,
  SPINNER: `${MAIN_CLASS}__spinner`,
  TABLE_WRAPPER: `${MAIN_CLASS}__table-wrapper`,
  TABLE_COMPLEX_NUMBER: `${MAIN_CLASS}__table__complex-number`,
};
type DropdownOption = {
  key: string;
  value: boolean;
  name: string;
};

const dropdownOptions: DropdownOption[] = [
  { key: 'all', value: false, name: 'All engagements that happened within' },
  { key: 'specific', value: true, name: 'Engagements on emails sent within' },
];

const engagementHeaders: TableHeader[] = [
  {
    name: <span>Name</span>,
    key: 'name',
    isVisible: true,
    isSortable: false,
    tooltip: 'The name of the experience',
  },
  {
    name: <span>Experience status</span>,
    key: 'journeyStatus',
    isVisible: true,
    isSortable: true,
    tooltip:
      'This is the status from the start of the experience . The possible statuses are; active, inactive, deleted',
  },
  {
    name: <span>Sent</span>,
    key: 'sent',
    isVisible: true,
    isSortable: true,
    tooltip: 'Total number of emails sent for this experience',
  },
  {
    name: <span>Opened</span>,
    key: 'opened',
    isVisible: true,
    isSortable: true,
    tooltip: 'Of the total emails sent, this is the number of emails that have been opened.',
  },
  {
    name: <span>Clicked</span>,
    key: 'clicked',
    isVisible: true,
    isSortable: true,
    tooltip: 'Of the total emails opened, this is the number of clicks within the body of the email.',
  },
  {
    name: <span>Unsubscribed</span>,
    key: 'unsubscribed',
    isVisible: true,
    isSortable: true,
    tooltip:
      'The number of users that clicked on the unsubscribe link. this does not include blocked or bounced emails.',
  },
];

const deliveryHeaders: TableHeader[] = [
  {
    name: <span>Name</span>,
    key: 'name',
    isVisible: true,
    isSortable: false,
    tooltip: 'The name of the experience',
  },
  {
    name: <span>Experience status</span>,
    key: 'journeyStatus',
    isVisible: true,
    isSortable: true,
    tooltip:
      'This is the status from the start of the experience . The possible statuses are; active, inactive, deleted',
  },
  {
    name: <span>Sent</span>,
    key: 'sent',
    isVisible: true,
    isSortable: true,
    tooltip: 'The total number of emails sent from Flybits to SendGrid',
  },
  {
    name: <span>Failed</span>,
    key: 'failed',
    isVisible: true,
    isSortable: true,
    tooltip: 'The total number of emails that failed to send from Flybits to SendGrid',
  },
  {
    name: <span>Delivered</span>,
    key: 'delivered',
    isVisible: true,
    isSortable: true,
    tooltip: 'The total number of emails that have been successfully delivered from Flybits to SendGrid',
  },
  {
    name: <span>Bounced</span>,
    key: 'bounced',
    isVisible: true,
    isSortable: true,
    tooltip: 'This is the total number of emails that SendGrid bounced because of the intended recipients status',
  },
];

const analyticsExperiencesApi = new AnalyticsExperiencesAPI();

type EmailEngagementEventsProps = {
  entities: EmailEntity[];
  handleHeaderSort?: (header: TableHeader) => void;
  orderBy: string;
  sortBy: string;
  isLoading: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  perPage: number;
  setPerPage: (num: number) => void;
  totalPageNumber: number;
};

const EmailDeliveryTotalHeaders = ['TOTAL SENT', 'TOTAL FAILED', 'TOTAL DELIVERED', 'TOTAL BOUNCED'];
const EmailEngagementTotalHeaders = ['TOTAL SENT', 'TOTAL OPENED', 'TOTAL CLICKS', 'OPEN RATE', 'TOTAL UNSUBSCRIBED'];

type EmailEntity =
  | EmailEngagementResponse['data']['entities'][number]
  | EmailDeliveryResponse['data']['entities'][number];

const fetchEmailEngagement = async (
  analyticsExperiencesApi: AnalyticsExperiencesAPI,
  page: number,
  perPage: number,
  dateRange: DateRange<Date>,
  search: string,
  sortBy: string,
  sortOrder: string,
  launchedWithinStartEnd: string,
): Promise<EmailEngagementData> => {
  const { data: tableRes } = await analyticsExperiencesApi.getEmailEngagement({
    start: Math.trunc((dateRange[0]?.getTime() ?? new Date().setHours(0, 0, 0, 0)) / 1000),
    end: Math.trunc((dateRange[1]?.getTime() ?? new Date().setHours(23, 59, 59, 999)) / 1000),
    offset: (page - 1) * perPage,
    limit: perPage,
    search,
    sortBy,
    sortOrder,
    launchedWithinStartEnd,
  });
  const { data: totals } = await analyticsExperiencesApi.getEmailEngagementTotals({
    start: Math.trunc((dateRange[0]?.getTime() ?? new Date().setHours(0, 0, 0, 0)) / 1000),
    end: Math.trunc((dateRange[1]?.getTime() ?? new Date().setHours(23, 59, 59, 999)) / 1000),
  });
  const { open_rate, ...restTotals } = totals;
  return {
    entities: tableRes.entities || [],
    pagination: tableRes.pagination,
    updatedAt: tableRes.updated_at,
    totals: {
      ...restTotals,
      openRate: open_rate,
    },
  };
};

const fetchEmailDelivery = async (
  analyticsExperiencesApi: AnalyticsExperiencesAPI,
  page: number,
  perPage: number,
  dateRange: DateRange<Date>,
  search: string,
  sortBy: string,
  sortOrder: string,
): Promise<EmailDeliveryData> => {
  const { data: tableRes } = await analyticsExperiencesApi.getEmailDelivery({
    start: Math.trunc((dateRange[0]?.getTime() ?? new Date().setHours(0, 0, 0, 0)) / 1000),
    end: Math.trunc((dateRange[1]?.getTime() ?? new Date().setHours(23, 59, 59, 999)) / 1000),
    offset: (page - 1) * perPage,
    limit: perPage,
    search,
    sortBy,
    sortOrder,
  });
  const { data: totals } = await analyticsExperiencesApi.getEmailDeliveryTotals();
  return {
    entities: tableRes.entities || [],
    pagination: tableRes.pagination,
    updatedAt: tableRes.updated_at,
    totals,
  };
};

const EmailEngagementTable: React.FC = () => {
  const [selectedReportTypeDropdown, setSelectedReportTypeDropdown] = useState(dropdownOptions[0]);
  const [selectedTab, setSelectedTab] = useState('engagement');
  const [search, setSearch] = useState('');
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [sortByColumn] = useState('name');
  const [sortOrder] = useState('desc');
  const [perPageEngagement, setPerPageEngagement] = useState(5);
  const [currentPageEngagement, setCurrentPageEngagement] = useState(1);
  const [perPageDelivery, setPerPageDelivery] = useState(5);
  const [currentPageDelivery, setCurrentPageDelivery] = useState(1);

  const debouncedSearch = useDebounce(search, 300);
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setHours(0, 0, 0, 0);
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);

  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);

  const [emailEngagementDateRange, setEmailEngagementDateRange] = useState<AdvancedDateRange>([
    sevenDaysAgo,
    endOfToday,
    7,
    'day',
  ]);
  const [emailDeliveredDateRange, setEmailDeliveredDateRange] = useState<AdvancedDateRange>([
    sevenDaysAgo,
    endOfToday,
    7,
    'day',
  ]);

  const [emailEngagementQueryDateRange, setEmailEngagementQueryDateRange] = useState<AdvancedDateRange>([
    sevenDaysAgo,
    endOfToday,
  ]);
  const [emailDeliveredQueryDateRange, setEmailDeliveredQueryDateRange] = useState<AdvancedDateRange>([
    sevenDaysAgo,
    endOfToday,
  ]);

  const handleEmailEngagementDateRangeChange = (newDate: AdvancedDateRange) => {
    setEmailEngagementDateRange(newDate);
    const hasFullRange = newDate[0] !== null && newDate[1] !== null;

    if (hasFullRange) {
      const endOfDay = new Date(newDate[1] ?? new Date());
      if (newDate.length < 3 || (newDate.length >= 3 && newDate[2] === 0)) {
        // Known interval - no custom or single day
        endOfDay.setHours(23, 59, 59, 999);
      }
      setEmailEngagementQueryDateRange([newDate[0], endOfDay]);
    }
  };

  const handleEmailDeliveryDateRangeChange = (newDate: AdvancedDateRange) => {
    setEmailDeliveredDateRange(newDate);
    const hasFullRange = newDate[0] !== null && newDate[1] !== null;

    if (hasFullRange) {
      const endOfDay = new Date(newDate[1] ?? new Date());
      if (newDate.length < 3 || (newDate.length >= 3 && newDate[2] === 0)) {
        // Known interval - no custom or single day
        endOfDay.setHours(23, 59, 59, 999);
      }
      setEmailDeliveredQueryDateRange([newDate[0], endOfDay]);
    }
  };

  const confirmationDialogProps = useMemo<ConfirmationDialogProps>(
    () =>
      selectedTab === 'engagement'
        ? {
            icon: <IconDownloadData />,
            title: 'Download email engagement',
            description:
              'Note that the engagement table will be downloaded based on your current filters. The format will be in CSV.',
            primaryAction: { value: 'Download' },
            secondaryAction: { value: 'Cancel' },
          }
        : {
            icon: <IconDownloadData />,
            title: 'Download email delivery',
            description:
              'Note that the delivery table will be downloaded based on your current filters. The format will be in CSV.',
            primaryAction: { value: 'Download' },
            secondaryAction: { value: 'Cancel' },
          },
    [selectedTab],
  );

  const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = evt.target;
    setSearch(value);
    setCurrentPageEngagement(1); // Reset to first page on search change
    setCurrentPageDelivery(1); // Reset to first page on search change
  };

  // Use the custom hook for Engagement Events table
  const {
    sortByColumn: sortByEngagementEmailColumn,
    sortOrder: orderByEngagementEmailColumn,
    handleHeaderSort: handleEngagementHeaderSort,
  } = useTableSort('name', 'desc');

  const [emailDataForCSV, setEmailDataForCSV] = useState<Record<string, string>[]>([]);
  const [isAllEmailDataLoading, setAllEmailDataLoading] = useState(false);

  // Use the custom hook for Delivery Events table
  const {
    sortByColumn: sortByEngagementDeliveredColumn,
    sortOrder: orderByEngagementDeliveredColumn,
    handleHeaderSort: handleDeliveryHeaderSort,
  } = useTableSort('name', 'desc');

  const { data: engagement, isFetching: isEngagementFetching } = useQuery({
    queryKey: [
      'email-engagement',
      sortByEngagementEmailColumn,
      orderByEngagementEmailColumn,
      emailEngagementQueryDateRange,
      debouncedSearch,
      selectedReportTypeDropdown.key,
      currentPageEngagement,
    ],
    queryFn: () =>
      fetchEmailEngagement(
        analyticsExperiencesApi,
        currentPageEngagement, // page
        perPageEngagement, // perPage
        [emailEngagementQueryDateRange[0], emailEngagementQueryDateRange[1]], // dateRange
        debouncedSearch,
        sortByEngagementEmailColumn, // sortBy
        orderByEngagementEmailColumn, // sortOrder
        selectedReportTypeDropdown.key === 'all' ? TIME_CONTEXT.contextTime : TIME_CONTEXT.journeyLaunch,
      ),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const { data: delivery, isFetching: isDeliveryFetching } = useQuery({
    queryKey: [
      'email-delivery',
      sortByEngagementDeliveredColumn,
      orderByEngagementDeliveredColumn,
      emailDeliveredQueryDateRange,
      debouncedSearch,
      currentPageDelivery,
    ],
    queryFn: () =>
      fetchEmailDelivery(
        analyticsExperiencesApi,
        currentPageDelivery, // page
        perPageDelivery, // perPage
        [emailDeliveredQueryDateRange[0], emailDeliveredQueryDateRange[1]], // dateRange
        debouncedSearch,
        sortByEngagementDeliveredColumn, // sortBy
        orderByEngagementDeliveredColumn, // sortOrder
      ),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const [DownloadConfirmModal, showDownloadConfirmModal] = useConfirmModal(confirmationDialogProps);

  const generateEmailEngagementCSVRecords = (data: EmailEngagementData): Record<string, string>[] => {
    return data.entities.map((emailEngagementEntity) => {
      return {
        Name: emailEngagementEntity.name,
        'Experience Status': emailEngagementEntity.journeyStatus ?? '',
        Sent: emailEngagementEntity.metrics.sent.toLocaleString(),
        Opened: emailEngagementEntity.metrics.opened.toLocaleString(),
        Clicked: emailEngagementEntity.metrics.clicked.toLocaleString(),
        Unsubscribed: emailEngagementEntity.metrics.unsubscribed.toLocaleString(),
      };
    });
  };

  const generateEmailDeliveryCSVRecords = (data: EmailDeliveryData): Record<string, string>[] => {
    return data.entities.map((emailDeliveryEntity) => {
      return {
        Name: emailDeliveryEntity.name,
        'Experience Status': emailDeliveryEntity.journeyStatus ?? '',
        Sent: emailDeliveryEntity.metrics.sent.toLocaleString(),
        Failed: emailDeliveryEntity.metrics.failed.toLocaleString(),
        Delivered: emailDeliveryEntity.metrics.delivered.toLocaleString(),
        Bounced: emailDeliveryEntity.metrics.bounced.toLocaleString(),
      };
    });
  };

  const handleClickDownload = useCallback(async () => {
    try {
      if (await showDownloadConfirmModal()) {
        setAllEmailDataLoading(true);
        let allData;
        switch (selectedTab) {
          case 'engagement':
            // TODO: Fetch email engagement data depending on the selected date range  https://flybits.atlassian.net/browse/PET-4569
            allData = await queryClient.fetchQuery({
              queryKey: [
                'email-engagement',
                sortByEngagementEmailColumn,
                orderByEngagementEmailColumn,
                debouncedSearch,
              ],
              queryFn: () =>
                fetchEmailEngagement(
                  analyticsExperiencesApi,
                  1,
                  100000, // Sending higher limit to BE to fetch all records
                  [emailEngagementDateRange[0], emailEngagementDateRange[1]], // dateRange
                  debouncedSearch,
                  sortByColumn,
                  sortOrder,
                  selectedReportTypeDropdown.key === 'all' ? TIME_CONTEXT.contextTime : TIME_CONTEXT.journeyLaunch,
                ),
            });
            if (!allData || !allData.entities || !allData.entities.length) {
              dispatch({
                type: 'SHOW_SNACKBAR',
                payload: {
                  title: 'Info',
                  content: 'No Email engagement data found',
                  type: 'info',
                },
              });
            } else {
              setEmailDataForCSV(generateEmailEngagementCSVRecords(allData));
              setTimeout(() => setEmailDataForCSV([]), 100);
            }
            break;
          case 'delivered':
            // TODO: Fetch email delivery data depending on the selected date range  https://flybits.atlassian.net/browse/PET-4569
            allData = await queryClient.fetchQuery({
              queryKey: [
                'email-delivery',
                sortByEngagementDeliveredColumn,
                orderByEngagementDeliveredColumn,
                debouncedSearch,
              ],
              queryFn: () =>
                fetchEmailDelivery(
                  analyticsExperiencesApi,
                  1,
                  100000, // Sending higher limit to BE to fetch all records
                  [emailDeliveredDateRange[0], emailDeliveredDateRange[1]], // dateRange
                  debouncedSearch,
                  sortByColumn,
                  sortOrder,
                ),
            });
            if (!allData || !allData.entities || !allData.entities.length) {
              dispatch({
                type: 'SHOW_SNACKBAR',
                payload: {
                  title: 'Info',
                  content: 'No Email delivery data found',
                  type: 'info',
                },
              });
            } else {
              setEmailDataForCSV(generateEmailDeliveryCSVRecords(allData));
              setTimeout(() => setEmailDataForCSV([]), 100);
            }
            break;
        }
      }
    } catch {
      dispatch({
        type: 'SHOW_SNACKBAR',
        payload: {
          title: 'Error',
          content: 'Something went wrong and could not download Email engagement/delivery data.',
          type: 'error',
        },
      });
    } finally {
      setAllEmailDataLoading(false);
    }
  }, [
    debouncedSearch,
    dispatch,
    emailDeliveredDateRange,
    emailEngagementDateRange,
    orderByEngagementDeliveredColumn,
    orderByEngagementEmailColumn,
    queryClient,
    selectedReportTypeDropdown.key,
    selectedTab,
    showDownloadConfirmModal,
    sortByColumn,
    sortByEngagementDeliveredColumn,
    sortByEngagementEmailColumn,
    sortOrder,
  ]);

  const EmailDeliveryTotalValues = [
    formatTotalValue(delivery?.totals?.sent ?? 0),
    formatTotalValue(delivery?.totals?.failed ?? 0),
    formatTotalValue(delivery?.totals?.delivered ?? 0),
    formatTotalValue(delivery?.totals?.bounced ?? 0),
  ];
  const EmailEngagementTotalValues = [
    formatTotalValue(engagement?.totals?.sent ?? 0),
    formatTotalValue(engagement?.totals?.opened ?? 0),
    formatTotalValue(engagement?.totals?.clicked ?? 0),
    `${formatTotalValue(engagement?.totals?.openRate ?? 0)}%`,
    formatTotalValue(engagement?.totals?.unsubscribed ?? 0),
  ];

  return (
    <div className={MAIN_CLASS}>
      <div className={CLASSES.HEADER}>
        <h2>
          <div className={CLASSES.TITLE}>
            <IconMessage aria-hidden="true" /> Email
          </div>
          {selectedTab === 'engagement' && engagement?.updatedAt ? (
            <div className={CLASSES.LAST_UPDATED}>
              <IconLastUpdated aria-hidden="true" /> Last updated {timeAgo(engagement.updatedAt * 1000)}
            </div>
          ) : null}
          {selectedTab === 'delivered' && delivery?.updatedAt ? (
            <div className={CLASSES.LAST_UPDATED}>
              <IconLastUpdated aria-hidden="true" /> Last updated {timeAgo(delivery.updatedAt * 1000)}
            </div>
          ) : null}
        </h2>
        <div className={CLASSES.TABS}>
          <FlightTabs
            labels={[
              { key: 'engagement', name: 'Engagement events' },
              { key: 'delivered', name: 'Delivered events' },
            ]}
            selectedTab={selectedTab}
            onSelect={(e: { key: string }) => {
              setSelectedTab(e.key);

              // Reset date pickers to last 7 days
              const sevenDaysAgo = new Date();
              sevenDaysAgo.setHours(0, 0, 0, 0);
              sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);

              const todayEnd = new Date();
              todayEnd.setHours(23, 59, 59, 999);

              setEmailEngagementDateRange([sevenDaysAgo, todayEnd, 7, 'day']);
              setEmailEngagementQueryDateRange([sevenDaysAgo, todayEnd]);
              setEmailDeliveredDateRange([sevenDaysAgo, todayEnd, 7, 'day']);
              setEmailDeliveredQueryDateRange([sevenDaysAgo, todayEnd]);
            }}
            orientation={'left'}
          />
        </div>
      </div>
      <div className={CLASSES.HEADER}>
        {selectedTab === 'engagement' && (
          <div className={CLASSES.REPORT_TYPE}>
            <FlightSelect
              label="Report type"
              width={312}
              options={dropdownOptions}
              selected={selectedReportTypeDropdown}
              handleOptionClick={(option: DropdownOption) => {
                setSelectedReportTypeDropdown(option);
              }}
            />
          </div>
        )}
        <div>
          {selectedTab === 'engagement' && (
            <FlightDateRangePicker
              className={CLASSES.DATE_RANGE_DD}
              value={emailEngagementDateRange}
              onChange={handleEmailEngagementDateRangeChange}
              maxDate={new Date()}
              includeAllTime={false}
              includeToday
              isIncludeCustom
              replaceCustomRangeLabel={false}
            />
          )}
          {selectedTab === 'delivered' && (
            <FlightDateRangePicker
              className={CLASSES.DATE_RANGE_DD}
              value={emailDeliveredDateRange}
              onChange={handleEmailDeliveryDateRangeChange}
              maxDate={new Date()}
              includeAllTime={false}
              includeToday
              isIncludeCustom
              replaceCustomRangeLabel={false}
            />
          )}
        </div>
        <div className={CLASSES.DATE_RANGE}>
          {selectedTab === 'engagement' &&
            (emailEngagementDateRange[2] === 0
              ? `${epochToDateTimeString(new Date().getTime(), 'en-US', DATE_FORMAT_OPTIONS)}`
              : `${epochToDateTimeString(
                  emailEngagementDateRange[0] || 0,
                  'en-US',
                  DATE_FORMAT_OPTIONS,
                )} - ${epochToDateTimeString(emailEngagementDateRange[1] || 0, 'en-US', DATE_FORMAT_OPTIONS)}`)}

          {selectedTab === 'delivered' &&
            (emailDeliveredDateRange[2] === 0
              ? `${epochToDateTimeString(new Date().getTime(), 'en-US', DATE_FORMAT_OPTIONS)}`
              : `${epochToDateTimeString(
                  emailDeliveredDateRange[0] || 0,
                  'en-US',
                  DATE_FORMAT_OPTIONS,
                )} - ${epochToDateTimeString(emailDeliveredDateRange[1] || 0, 'en-US', DATE_FORMAT_OPTIONS)}`)}
        </div>
        <div className={CLASSES.DOWNLOAD}>
          <DownloadEngagementData
            data={emailDataForCSV}
            type={
              selectedTab === 'engagement'
                ? ENGAGEMENT_DATA_TYPES.EMAIL_ENGAGEMENT
                : ENGAGEMENT_DATA_TYPES.EMAIL_DELIVERY
            }
            isDownloading={isAllEmailDataLoading}
            onClickDownload={handleClickDownload}
          />
        </div>
      </div>
      <TotalHeader
        headers={selectedTab === 'delivered' ? EmailDeliveryTotalHeaders : EmailEngagementTotalHeaders}
        values={selectedTab === 'delivered' ? EmailDeliveryTotalValues : EmailEngagementTotalValues}
        isLoading={selectedTab === 'engagement' ? isEngagementFetching : isDeliveryFetching}
      />
      <div className={CLASSES.SEARCH}>
        <div className={CLASSES.SEARCH_WRAPPER}>
          <FlightTextInput
            onChange={handleSearchChange}
            value={search}
            placeholderText="Search"
            iconInput="search"
            width="280px"
            hasClearIcon
          />
        </div>
        <div className={CLASSES.PROVIDER}>
          <SendGridProvider /> Data source by SendGrid
        </div>
      </div>
      <div className={CLASSES.TABLE_WRAPPER}>
        {selectedTab === 'engagement' && (
          <FlightTabPanel>
            <EmailEngagementEngagementEvents
              handleHeaderSort={handleEngagementHeaderSort}
              sortBy={sortByEngagementEmailColumn}
              orderBy={orderByEngagementEmailColumn}
              entities={engagement?.entities ?? []}
              isLoading={isEngagementFetching}
              currentPage={currentPageEngagement}
              setCurrentPage={setCurrentPageEngagement}
              perPage={perPageEngagement}
              setPerPage={setPerPageEngagement}
              totalPageNumber={Math.ceil((engagement?.pagination?.totalRecords ?? 0) / perPageEngagement) || 1}
            />
          </FlightTabPanel>
        )}
        {selectedTab === 'delivered' && (
          <FlightTabPanel>
            <EmailEngagementDeliveryEvents
              handleHeaderSort={handleDeliveryHeaderSort}
              orderBy={orderByEngagementDeliveredColumn}
              sortBy={sortByEngagementDeliveredColumn}
              entities={delivery?.entities ?? []}
              isLoading={isDeliveryFetching}
              currentPage={currentPageDelivery}
              setCurrentPage={setCurrentPageDelivery}
              perPage={perPageDelivery}
              setPerPage={setPerPageDelivery}
              totalPageNumber={Math.ceil((delivery?.pagination?.totalRecords ?? 0) / perPageDelivery) || 1}
            />
          </FlightTabPanel>
        )}
      </div>
      <DownloadConfirmModal />
    </div>
  );
};

interface EmailEngagementTableProps<T extends EmailEntity> {
  entities: T[];
  headers: TableHeader[];
  getRow: (entity: T) => TableRow;
  handleHeaderSort?: (key: TableHeader) => void;
  sortOrder: string | 'asc' | 'desc';
  sortBy: string;
  loading: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  perPage: number;
  setPerPage: (num: number) => void;
  totalPageNumber: number;
}

const EmailEngagementGenericTable = <T extends EmailEntity>({
  entities,
  headers,
  getRow,
  handleHeaderSort,
  sortOrder,
  sortBy,
  loading,
  currentPage,
  setCurrentPage,
  perPage,
  setPerPage,
  totalPageNumber,
}: EmailEngagementTableProps<T>) => {
  return (
    <>
      <FlightTable
        tableHeaders={headers}
        hasPaginationBeforeTable={false}
        hasPaginationAfterTable={!!entities.length && !loading}
        tableData={entities.map(getRow)}
        handleHeaderSort={handleHeaderSort}
        sortOrder={sortOrder}
        sortByKey={sortBy}
        isLoading={loading}
        paginationProps={{
          totalPageNumber: totalPageNumber,
          currentPageNumber: currentPage,
          rowsPerPageOptions: [5, 10, 15, 20],
          currentRowsPerPage: perPage,
          handlePageChange: (page: number) => setCurrentPage(page),
          handleRowsPerPageChange: (num: number) => setPerPage(num),
        }}
      />
      {!entities.length && !loading && (
        <div className={CLASSES.EMPTY_DATA}>
          {getIcon('search', {})}
          No results found
        </div>
      )}
    </>
  );
};

const engagementRow = (entity: EmailEngagementResponse['data']['entities'][number]): TableRow => {
  const statusBadgeType =
    Object.values(STATUS_TYPES).find((p) => p === entity.journeyStatus) || STATUS_TYPES.STATUS_ACTIVE;
  return {
    name: entity.name,
    journeyStatus: <StatusBadge status={statusBadgeType} title={entity.journeyStatus ?? ''} showTooltip={false} />,
    sent: intlNumberFormat(entity.metrics.sent),
    opened: intlNumberFormat(entity.metrics.opened),
    clicked: intlNumberFormat(entity.metrics.clicked),
    unsubscribed: intlNumberFormat(entity.metrics.unsubscribed),
  };
};

const deliveryRow = (entity: EmailDeliveryResponse['data']['entities'][number]): TableRow => {
  const statusBadgeType =
    Object.values(STATUS_TYPES).find((p) => p === entity.journeyStatus) || STATUS_TYPES.STATUS_ACTIVE;
  return {
    name: entity.name,
    journeyStatus: <StatusBadge status={statusBadgeType} title={entity.journeyStatus ?? ''} showTooltip={false} />,
    sent: intlNumberFormat(entity.metrics.sent),
    failed: intlNumberFormat(entity.metrics.failed),
    delivered: intlNumberFormat(entity.metrics.delivered),
    bounced: intlNumberFormat(entity.metrics.bounced),
  };
};

const EmailEngagementEngagementEvents: React.FC<EmailEngagementEventsProps> = ({
  entities,
  handleHeaderSort,
  orderBy,
  sortBy,
  isLoading,
  currentPage,
  setCurrentPage,
  perPage,
  setPerPage,
  totalPageNumber,
}) => (
  <EmailEngagementGenericTable
    entities={entities as EmailEngagementResponse['data']['entities']}
    headers={engagementHeaders}
    getRow={engagementRow}
    handleHeaderSort={handleHeaderSort}
    sortBy={sortBy}
    sortOrder={orderBy}
    loading={isLoading}
    currentPage={currentPage}
    setCurrentPage={setCurrentPage}
    perPage={perPage}
    setPerPage={setPerPage}
    totalPageNumber={totalPageNumber}
  />
);

const EmailEngagementDeliveryEvents: React.FC<EmailEngagementEventsProps> = ({
  entities,
  handleHeaderSort,
  orderBy,
  sortBy,
  isLoading,
  currentPage,
  setCurrentPage,
  perPage,
  setPerPage,
  totalPageNumber,
}) => (
  <EmailEngagementGenericTable
    entities={entities as EmailDeliveryResponse['data']['entities']}
    headers={deliveryHeaders}
    getRow={deliveryRow}
    handleHeaderSort={handleHeaderSort}
    sortBy={sortBy}
    sortOrder={orderBy}
    loading={isLoading}
    currentPage={currentPage}
    setCurrentPage={setCurrentPage}
    perPage={perPage}
    setPerPage={setPerPage}
    totalPageNumber={totalPageNumber}
  />
);

export default EmailEngagementTable;
